import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../../shared/ui/kit/utils';
import type { SelectOption } from '../types';

interface SelectSettingProps {
    value: string;
    options: SelectOption[];
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const SelectSetting: React.FC<SelectSettingProps> = ({
    value,
    options,
    onChange,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectedOption = options.find(o => o.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all',
                    'bg-[var(--bg)] border-[var(--border)] text-[var(--text)]',
                    'hover:border-[var(--border-strong)]',
                    'focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand-dim)]',
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
            >
                <span>{selectedOption?.label || 'Выбрать...'}</span>
                <motion.svg
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-[var(--text-muted)]"
                >
                    <polyline points="6 9 12 15 18 9" />
                </motion.svg>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className={cn(
                            'absolute top-full right-0 mt-1 min-w-[160px] z-50',
                            'bg-[var(--bg-elevated)] border border-[var(--border)] rounded-lg shadow-lg',
                            'overflow-hidden'
                        )}
                    >
                        {options.map((option, index) => (
                            <motion.button
                                key={option.value}
                                type="button"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.03 }}
                                onClick={() => handleSelect(option.value)}
                                className={cn(
                                    'w-full px-3 py-2 text-left text-xs transition-colors',
                                    'hover:bg-[var(--bg-hover)]',
                                option.value === value && 'bg-[var(--brand-dim)] text-[var(--brand)] font-medium'
                                )}
                            >
                                {option.label}
                            </motion.button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};